/**
 * The association projection for the calendar's `entity_kind: 'event'` queries.
 *
 * This replaces `include_associated_objects: true`, which asks the backend for
 * EVERY association type on the entity. That path makes the backend run the
 * association-target accessibility fan-out over every type registered in the
 * org (`query_service._gate_association_targets(None, ...)`), then hydrate and
 * ship every target — whether or not the calendar reads any of it. `ahoy_meeting`
 * is the highest-degree entity we render, so it is the worst possible entity to
 * ask for all-types on. `lib/dashboard/queries.ts` made this same move for the
 * dashboard; the calendar was left behind.
 *
 * Measured on QA org 13 (173 association types), five notes-bearing meetings
 * addressed by id: all-types took 6.86s and hydrated 19 objects; this projection
 * took 0.57s and hydrated 8, with identical rendering inputs. Over a week of the
 * calendar the all-types path was also shipping seven `ahoy_research` meeting-prep
 * documents that no calendar code path opens.
 *
 * Every entry below is justified by a real read. Do not add a type without a call
 * site, and do not remove one without checking the call site named beside it.
 */
export const CALENDAR_EVENT_ASSOCIATIONS: Record<
  string,
  { properties: string[] }
> = {
  // mapAssociatedUsers -> CalendarEvent.users (utils/eventMapper.ts). Reads both
  // of these properties off the hydrated target.
  ahoy_meeting_to_user: {
    properties: ['ahoy_display_name', 'ahoy_avatar_url'],
  },
  // Existence only, but hasReadableMeetingNotes (utils/eventMapper.ts) keys on
  // the target RESOLVING in associated_objects rather than on the edge — the
  // backend permission-checks only the association's source, so an edge alone
  // would leak the existence of a summary the user cannot read. `properties: []`
  // still hydrates the target (verified against QA: 5/5 notes edges resolved),
  // it just contributes nothing to the property union.
  ahoy_meeting_to_meeting_notes: { properties: [] },
  // Drives the reminder's title and its navigation target (utils/reminderUtils.ts).
  ahoy_reminder_to_subject: { properties: ['ahoy_display_name'] },
  // reminderOwnerEntityId (utils/reminderUtils.ts) reads target_entity_id off the
  // EDGE and never touches the object, so no properties are needed — but the type
  // MUST stay listed. Drop it and the edge disappears, every reminder reads as
  // unowned, and isReminderVisibleToUser shows the whole org's reminders.
  ahoy_reminder_to_owner: { properties: [] },
};
